import type Decimal from 'decimal.js';
import { ArrowRightLeft, Check, Pencil, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, NumberInput, Skeleton } from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useBaseCurrency } from '../../lib/currenciesStore.js';
import { effectiveRateFor, formatRate, loadExchangeRates, saveExchangeRate, type RateRow } from '../../lib/exchangeRates.js';
import { formatDate } from '../../lib/intl.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';

interface CurrencyItem { code: string; isActive: boolean }

/**
 * Today's exchange rate, on the dashboard, editable in place.
 *
 * The rate table already drives everything — GL posting, cross-currency stock
 * blends, treasury transfers — but the only places that could WRITE it were
 * buried inside the manual-journal and transfer forms, so "change today's
 * dollar rate" meant opening a journal you didn't want. This card is the
 * owner's morning ritual made first-class: see 1 USD = X IQD, tap the pencil,
 * type the day's number, done.
 *
 * One row per active non-base currency; the card disappears entirely for
 * single-currency tenants and for anyone without `exchange_rates.read`.
 * Saving posts the same `POST /exchange-rates` (CUR→BASE, as-of midnight UTC
 * today) the journal form uses — same table, same cutoff rule, so a rate set
 * here is exactly what tonight's postings will pick up.
 */
export function ExchangeRateCard({ baseCurrency: fallbackBase }: { baseCurrency: string }): JSX.Element | null {
  const { t, i18n } = useTranslation();
  // The server's word on the shop's currency first (see useBaseCurrency): the
  // dashboard's own figure falls back to a guess when its overview is missing.
  const baseCurrency = useBaseCurrency() ?? fallbackBase;
  const canRead = usePermission('exchange_rates.read');
  const canWrite = usePermission('exchange_rates.write');

  const [currencies, setCurrencies] = useState<string[] | null>(null);
  const [rates, setRates] = useState<RateRow[] | null>(null);
  /** Currency code being edited, and the raw text typed so far. */
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!canRead) return undefined;
    let on = true;
    void api().get<{ items: CurrencyItem[] }>('/currencies')
      .then((r) => { if (on) setCurrencies(r.data.items.filter((c) => c.isActive && c.code !== baseCurrency).map((c) => c.code)); })
      .catch(() => { if (on) setCurrencies([]); });
    void loadExchangeRates().then((r) => { if (on) setRates(r); });
    return () => { on = false; };
  }, [canRead, baseCurrency]);

  if (!canRead) return null;
  const loading = currencies === null || rates === null;
  // A shop that trades in one currency has no rate to keep — no card, no noise.
  if (!loading && currencies.length === 0) return null;

  // The card is the WORKING set, not the catalogue: only currencies that have
  // ever been given a rate (either direction). A tenant with many activated
  // currencies would otherwise render a wall of "—" rows. When nothing has a
  // rate yet, fall back to all active ones so the very first rate can still be
  // typed here.
  const shown = ((): string[] => {
    if (loading) return [];
    const rated = currencies.filter((code) => rates.some(
      (r) => (r.fromCurrency === code && r.toCurrency === baseCurrency) || (r.fromCurrency === baseCurrency && r.toCurrency === code),
    ));
    return (rated.length > 0 ? rated : currencies).slice(0, 6);
  })();

  const todayStr = new Date().toISOString().slice(0, 10);

  const startEdit = (code: string, current: Decimal | null): void => {
    setEditing(code);
    setDraft(current ? current.toDecimalPlaces(8).toString() : '');
  };

  const save = async (code: string): Promise<void> => {
    const clean = normalizeNumericInput(draft);
    const val = safeDecimal(clean);
    if (val.lte(0)) return;
    setSaving(true);
    try {
      await saveExchangeRate(code, baseCurrency, val.toDecimalPlaces(8).toString(), todayStr);
      setEditing(null);
      setRates(await loadExchangeRates());
      toast.success(t('dashboard.rateSaved', { defaultValue: 'Rate saved — used by everything posted from today on.' }));
    } catch {
      toast.error(t('dashboard.rateFailed', { defaultValue: "Couldn't save the rate." }));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="flex flex-col gap-3 p-4">
      <div className="flex items-center gap-2">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-brand/10 text-brand">
          <ArrowRightLeft className="h-4 w-4" aria-hidden />
        </span>
        <h2 className="text-sm font-semibold">{t('dashboard.exchangeRate', { defaultValue: 'Exchange rate' })}</h2>
      </div>

      {loading ? (
        <Skeleton className="h-9 w-full" />
      ) : shown.map((code) => {
        const current = effectiveRateFor(rates, code, baseCurrency, todayStr);
        // The as-of date of the rate actually in force — stale is worth seeing:
        // "1500, set 5 weeks ago" is the prompt to update it.
        const row = rates
          .filter((r) => (r.fromCurrency === code && r.toCurrency === baseCurrency) || (r.fromCurrency === baseCurrency && r.toCurrency === code))
          .filter((r) => new Date(r.asOf).getTime() <= new Date(`${todayStr}T00:00:00.000Z`).getTime())
          .sort((a, b) => new Date(b.asOf).getTime() - new Date(a.asOf).getTime())[0];
        const isEditing = editing === code;
        return (
          <div key={code} className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm text-fg-muted">1 {code} =</span>
            {isEditing ? (
              <>
                <div className="w-32">
                  <NumberInput
                    autoFocus
                    className="text-end font-mono"
                    value={draft}
                    onValueChange={setDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); void save(code); }
                      if (e.key === 'Escape') setEditing(null);
                    }}
                    aria-label={t('dashboard.exchangeRate', { defaultValue: 'Exchange rate' })}
                  />
                </div>
                <span className="text-sm text-fg-muted">{baseCurrency}</span>
                <Button type="button" size="sm" loading={saving} onClick={() => void save(code)} aria-label={t('actions.save')}>
                  <Check className="h-4 w-4" aria-hidden />
                </Button>
                <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={() => setEditing(null)} aria-label={t('actions.cancel')}>
                  <X className="h-4 w-4" aria-hidden />
                </Button>
              </>
            ) : (
              <>
                <span className="font-mono text-xl font-bold">
                  {current ? formatRate(current, i18n.language) : '—'}
                </span>
                <span className="text-sm text-fg-muted">{baseCurrency}</span>
                {row && (
                  <span className="text-xs text-fg-subtle">
                    {formatDate(row.asOf, { locale: i18n.language })}
                  </span>
                )}
                {canWrite && (
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => startEdit(code, current)}
                    aria-label={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                    title={t('dashboard.editRate', { defaultValue: 'Change the rate' })}
                  >
                    <Pencil className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </>
            )}
          </div>
        );
      })}
    </Card>
  );
}
